import React, { useEffect, useState, useMemo, useContext } from "react";
import { TextField, Button } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateIcon from "@mui/icons-material/Create";

import Swal from "sweetalert2";

import DataTable from "react-data-table-component";
import moment from "moment";
import Toast from "../../utils/Toast";

import ExpandedAudit from "../../components/ExpandedAudit";
import { AuthContext } from "../../utils/userProvider";

import usePendingAudits from "../../queries/audit/getPendingAudits";
import useApproveAudit from "../../mutation/audit/approveAudit";
import useRejectAudit from "../../mutation/audit/rejectAudit";
import useProperties from "../../queries/property/getProperties";
import useCompanies from "../../queries/gift/companies";
import useRoomTypes from "../../queries/roomType/getRoomTypeLinks";
import useGifts from "../../queries/gift/getGifts";
import useCities from "../../queries/city/getCities";
import useRateTypes from "../../queries/roomType/getRateTypes";
import parseRequirements from "../../utils/parseRequirements";
import Loader from "../../components/Loader";
import {
  ListPageShell,
  ListPageCard,
  ListPageHeader,
  dataTableCustomStyles,
} from "../../components/ListPage";

const PendingAudits = () => {
  const [transactionTable, setTransactionTable] = useState([]);

  const [approveResponse, setApproveData] = useApproveAudit();
  const [rejectResponse, setRejectData] = useRejectAudit();
  const { authState, setAuthState } = useContext(AuthContext);

  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  useEffect(() => {
    if (approveResponse.data) {
      Toast.fire({
        icon: "success",
        title: "Audit Approved",
      });
      reset();
    } else if (approveResponse.error) {
      let data = approveResponse.errorMessage.response.data;
      if (data) {
        let messageData = data.message;
        let message = messageData.message;
        Swal.fire({
          icon: "error",
          title: "Error",
          text: message,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "An errror occured, please try again later",
        });
      }
    }
    if (rejectResponse.data) {
      Toast.fire({
        icon: "success",
        title: "Audit Rejected",
      });
      reset();
    } else if (rejectResponse.error) {
      let data = rejectResponse.errorMessage.response.data;
      if (data) {
        let messageData = data.message;
        let message = messageData.message;
        Swal.fire({
          icon: "error",
          title: "Error",
          text: message,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "An errror occured, please try again later",
        });
      }
    }
  }, [approveResponse, rejectResponse]);

  function approveAudit(row) {
    Swal.fire({
      title: "Approve Updates",
      html: `<h4>Please provide a description explaining the approval</h4>
            <textarea maxlength="100" type="text" id="resolutionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
      confirmButtonText: "Confirm",
      focusConfirm: false,
      preConfirm: () => {
        const resolutionDescription = Swal.getPopup().querySelector(
          "#resolutionDescription"
        ).value;
        if (!resolutionDescription) {
          Swal.showValidationMessage(`Please enter description`);
        }
        return {
          resolutionDescription: resolutionDescription,
          id: row.id,
        };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setApproveData(result.value);
      }
    });
  }

  function rejectAudit(row) {
    Swal.fire({
      title: "Reject Updates",
      html: `<h4>Please provide a description explaining the rejection</h4>
            <textarea maxlength="100" type="text" id="resolutionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
      confirmButtonText: "Confirm",
      focusConfirm: false,
      preConfirm: () => {
        const resolutionDescription = Swal.getPopup().querySelector(
          "#resolutionDescription"
        ).value;
        if (!resolutionDescription) {
          Swal.showValidationMessage(`Please enter description`);
        }
        return {
          resolutionDescription: resolutionDescription,
          id: row.id,
        };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setRejectData(result.value);
      }
    });
  }

  function viewActionDescription(data) {
    console.log(data);
    Swal.fire({
      title: "Action Description",
      html: `<p style='white-space: pre-line; text-align: left'>${data}</p>`,
    });
  }

  let columns = [
    {
      name: "Executive",
      selector: "executive",
      sortable: true,
      cell: (row, index, column, id) => {
        if (row.executive) {
          return <span>{row.executive.email}</span>;
        } else {
          return <span>{"<DELETED>"}</span>;
        }
      },
    },
    {
      name: "Type",
      selector: "type",
      sortable: true,
      cell: (row, index, column, id) => {
        return <span>{capitalizeFirstLetter(row.type)}</span>;
      },
    },
    {
      name: "Action",
      selector: "action",
      sortable: true,
      cell: (row, index, column, id) => {
        return (
          <span>
            {row.action == "createManual"
              ? "Create Manual"
              : capitalizeFirstLetter(row.action)}
          </span>
        );
      },
    },
    {
      name: "Action Description",
      selector: "actionDescription",
      sortable: true,
      cell: (row, index, column, id) => {
        return (
          <div>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                viewActionDescription(row.actionDescription);
              }}
            >
              View Description
            </Button>
          </div>
        );
      },
    },
    {
      name: "Created At",
      selector: "createdAt",
      sortable: true,
      cell: (row, index, column, id) => {
        return (
          <span>{moment(row.createdAt).format("DD MMM YYYY hh:mmA")}</span>
        );
      },
    },
  ];

  if (authState.role != "Executive") {
    columns.push({
      name: "Options",
      sortable: true,
      cell: (row, index, column, id) => {
        return (
          <div>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<CreateIcon />}
              onClick={() => {
                approveAudit(row);
              }}
            >
              Approve
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => {
                rejectAudit(row);
              }}
            >
              Reject
            </Button>
          </div>
        );
      },
    });
  }

  const [response, reset] = usePendingAudits();
  const [properties, resetProperties] = useProperties();
  const [companies, resetCompanies] = useCompanies();
  const [roomTypes, resetRooms] = useRoomTypes();
  const [gifts, resetGifts] = useGifts();
  const [rateTypes, resetRateTypes] = useRateTypes();
  const [cities, resetCities] = useCities();

  const [filterText, setFilterText] = useState("");

  const handleFilterText = (event) => {
    setFilterText(event.target.value);
  };

  const filteredItems = transactionTable.filter((item) => {
    if (
      filterText != "" &&
      (authState.role != "Executive" || item.executive.id == authState.userId)
    ) {
      // Check for reservation ID in credit-notes type audits
      const reservationIdMatch = item.type === "credit-notes" && 
        item.snapshotCurrent?.transaction?.reservationId &&
        item.snapshotCurrent.transaction.reservationId.toString()
          .toLowerCase()
          .includes(filterText.toLowerCase());

      return (
        item.type?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.executive?.email
          .toLowerCase()
          .includes(filterText.toLowerCase()) ||
        item.action?.toLowerCase().includes(filterText.toLowerCase()) ||
        moment(item.createdAt)
          .format("DD MMM YYYY hh:mmA")
          .includes(filterText.toLowerCase()) ||
        reservationIdMatch
      );
    } else {
      return (
        authState.role != "Executive" || item.executive.id == authState.userId
      );
    }
  });

  useEffect(() => {
    if (response.data) {
      setTransactionTable([...response.data]);
    }
  }, [response]);

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div>
        <TextField
          id="filter-text"
          onChange={handleFilterText}
          label="Search"
          variant="outlined"
          size="small"
        />
      </div>
    );
  }, [filterText]);

  return (
    <ListPageShell>
      <ListPageCard>
        {(response.loading ||
          properties.loading ||
          companies.loading ||
          roomTypes.loading ||
          gifts.loading ||
          rateTypes.loading ||
          cities.loading) && <Loader />}
        {response.data &&
          properties.data &&
          companies.data &&
          roomTypes.data &&
          gifts.data &&
          rateTypes.data &&
          cities.data && (
            <div>
              <ListPageHeader title="Pending Audit Trails" />
              <DataTable
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 20, 30]}
                noHeader={true}
                subHeader
                data={filteredItems}
                columns={columns}
                expandableRows
                highlightOnHover
                expandOnRowClicked={true}
                expandableRowsComponent={
                  <ExpandedAudit
                    properties={properties.data}
                    companies={parseRequirements(companies.data)}
                    room_types={roomTypes.data}
                    gifts={gifts.data}
                    cities={cities.data}
                    rateTypes={rateTypes.data}
                  />
                }
                subHeaderComponent={subHeaderComponentMemo}
                customStyles={dataTableCustomStyles}
              />
            </div>
          )}
      </ListPageCard>
    </ListPageShell>
  );
};

export default PendingAudits;
